import type { AnnouncementWidget } from './AnnouncementWidget';
import {
    TimelineSlot,
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import { z } from 'zod';
import type { Announcement } from './Announcement';
import { zMarshalToBoolean } from '../../ReachApi/Marshallers';

export const DEFAULT_BACKGROUND_COLOR = '' as const;

export const SM_FACEBOOK = 'facebook' as const;
export const SM_INSTAGRAM = 'instagram' as const;
export const SM_TWITTER = 'twitter' as const;

export type AnnouncementTimelineSlot = TimelineSlot<Announcement, AnnouncementWidget>;
export type UnknownTimelineSlot = TimelineSlot;

export type AnnouncementWidgetTimelineSlotParams = Announcement;

export type AnnouncementWidgetTimelineSlot = TimelineSlot<
    AnnouncementWidgetTimelineSlotParams,
    AnnouncementWidget
>;

export const ZodAnnouncementWidgetConfigurationDetails =
    ZodGenericWidgetConfigurationDetails.extend({
        facilityid: z.string(),
        screenid: z.string(),
        background_color: z.string().nullish(),
        shuffle: z.unknown().transform(zMarshalToBoolean),
        mute_videos: z.unknown().transform(zMarshalToBoolean),
        is_new_media_library: z.unknown().transform(zMarshalToBoolean),
        filter_expression: z.string().nullish(),
        transform_method: z.string().nullish(),
    });

export type AnnouncementWidgetConfigurationDetails = z.infer<
    typeof ZodAnnouncementWidgetConfigurationDetails
>;

const ZodAnnouncementWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.ANNOUNCEMENT),
    config: ZodAnnouncementWidgetConfigurationDetails,
});

export type AnnouncementWidgetConfiguration = z.infer<typeof ZodAnnouncementWidgetConfiguration>;

export enum AnnouncementState {
    PROVISIONED = 'provisioned',
    UNPROVISIONED = 'unprovisioned',
}

export enum AnnouncementStatus {
    ACTIVE = 'active',
    APPROVED = 'approved',
    DELETED = 'deleted',
    DRAFT = 'draft',
    EXPIRED = 'expired',
    INACTIVE = 'inactive',
    NEED_APPROVAL = 'needapp',
}

export enum AnnouncementTemplates {
    INSTAGRAM = 'instagram',
    TWITTER = 'twitter',
    FACEBOOK = 'facebook',
    CONTENT = 'content',
    VIDEO = 'video',
    YOUTUBE = 'youtube',
    VIMEO = 'vimeo',
    InsufficientPlaylistRights = 'insufficient-playlist-rights',
    VideoNotSupported = 'video-not-supported',
    Default = 'default',
}

export enum Animation {
    Fade = 'fade',
    SlideRight = 'slide-right',
    SlideLeft = 'slide-left',
    SlideUp = 'slide-up',
    SlideDown = 'slide-down',
    None = '',
}

export const INSUFFICIENT_PLAYLIST_RIGHTS = 'insufficient-playlist-rights' as const;
export const VIDEO_NOT_SUPPORTED = 'video-not-supported' as const;
